import {
  PriceLabel,
  PriceLabelValue,
  ContainerAbsolute,
  TooltipPriceLabelWrapper,
  GridContainer,
  Margin20,
  QuantityLabel,
  DiscountLabel,
  DiscountValue,
  DiscountWrapper,
  CustomNetPriceWrapper,
  CustomNetPriceLabel,
  NetPriceWrapper,
} from './PricingSection.styled';
import { IFormattedPricingItem } from '../hooks';
import { useShouldShowSpecificPrices } from './hooks';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Tooltip from 'components/Tooltip/Tooltip';
import Icon from 'components/Icon/Icon';
import { Info, PencilSimple } from '@phosphor-icons/react';
import { COLORS } from 'assets/styled';
import EditDiscountModal, {
  DiscountsType,
} from 'components/Modals/EditDiscountModal/EditDiscountModal';
import useHasRole from 'utils/hooks/useHasRole';
import { PermissionGroupSupplierId } from 'types/Permissions.types';
import { useDispatch, useSelector } from 'react-redux';
import { IRootReducerState } from 'store/store';
import {
  getDiscountDisplay,
  hasAnyDiscounts,
  resetAllDiscounts,
  updateAllPricingWithDiscount,
  updatePricingWithDiscount,
} from './helpers';
import { setCustomNetPrice, setPricing } from 'store/Model/actions/model';
import { RoutesConfig } from 'navigation/routes';
import { useLocation } from 'react-router-dom';
import { useNavigateWithQuery } from 'utils/hooks/useNavigateWithQuery';
import FinalPriceModal from './FinalPriceModal/FinalPriceModal';
import { AppMode } from 'store/Common/reducers/common';
import { useFormatMoney } from 'utils/hooks/useFormatMoney';

interface IPricingSectionProps {
  formattedPrices: IFormattedPricingItem[];
  quantity: number;
  totalPrice: number;
}

interface IExistingDiscount {
  type: DiscountsType | null;
  value: string | null;
  gross: string | null;
}

const PricingSection = ({
  formattedPrices,
  quantity,
  totalPrice,
}: IPricingSectionProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const taxRate = queryParams.get('taxRate');
  const [isEditDiscountModalOpen, setIsEditDiscountModalOpen] = useState(false);
  const [isFinalPriceModalOpen, setIsFinalPriceModalOpen] = useState(false);
  const [applyToAll, setApplyToAll] = useState(false);
  const [existingDiscount, setExistingDiscount] = useState<IExistingDiscount>({
    type: null,
    value: null,
    gross: null,
  });

  const { currency } = useSelector(
    (state: IRootReducerState) => state.shoppingCartInfo
  );

  const formatMoney = useFormatMoney(currency);

  const [editingKey, setEditingKey] = useState<string | null>(null);
  const { showNet, showGross, isUsingSupplierSystemDiscounts, canEditPricing } =
    useShouldShowSpecificPrices();

  const navigateWithQuery = useNavigateWithQuery();

  const appMode: AppMode = useSelector(
    (state: IRootReducerState) => state.commonInfo.appMode
  );

  const pricing = useSelector(
    (state: IRootReducerState) => state.modelInfo.pricing
  );

  const handleConfirmDiscount = (
    discountValue: string,
    discountType: DiscountsType
  ) => {
    if (!editingKey) return;

    // Move user back on Config from the BOM or Specs page
    if (
      location.pathname === RoutesConfig.Bom.fullPath ||
      location.pathname === RoutesConfig.Specs.fullPath
    ) {
      navigateWithQuery(RoutesConfig.Configure.fullPath);
    }

    const updatedPricing = applyToAll
      ? updateAllPricingWithDiscount(pricing, discountValue, discountType)
      : updatePricingWithDiscount(
          pricing,
          editingKey,
          discountValue,
          discountType
        );

    dispatch(setCustomNetPrice(null));
    dispatch(setPricing(updatedPricing));
    setIsEditDiscountModalOpen(false);
  };

  const handleResetDiscount = () => {
    if (!editingKey) return;

    const clearedPricing = applyToAll
      ? resetAllDiscounts(pricing)
      : updatePricingWithDiscount(
          pricing,
          editingKey,
          null,
          DiscountsType.PERCENTAGE
        );

    dispatch(setCustomNetPrice(null));
    dispatch(setPricing(clearedPricing));
    setIsEditDiscountModalOpen(false);
  };

  const handleFinalPrice = (price: string) => {
    dispatch(setCustomNetPrice(Number(price)));
    setIsFinalPriceModalOpen(false);
  };

  // const canSetDiscount = useHasRole(
  //   undefined,
  //   PermissionGroupSupplierId.WRITE_CUSTOM_DISCOUNT_AND_AMOUNT
  // );

  const shouldShowDiscountColumn =
    showNet && showGross && hasAnyDiscounts(formattedPrices);

  return (
    <ContainerAbsolute
      externalMode={appMode === AppMode.EXTERNAL || taxRate != null}
    >
      <GridContainer
        areBothPriceTypesShown={showNet && showGross}
        hasDiscount={shouldShowDiscountColumn}
        onlyGrossValue={!showNet && showGross}
        onlyNetValue={showNet && !showGross}
      >
        <QuantityLabel>{quantity}x</QuantityLabel>
        {showGross && <PriceLabel>{t('Gross')}</PriceLabel>}
        <DiscountLabel hasDiscount={shouldShowDiscountColumn}>
          {shouldShowDiscountColumn ? t('Discount') : ''}
        </DiscountLabel>
        {showNet && (
          <PriceLabel>
            {isUsingSupplierSystemDiscounts ? t('Gross') : t('Net')}
          </PriceLabel>
        )}
        {formattedPrices.map(
          (formattedPrice: IFormattedPricingItem, index: number) => {
            const shouldShowPricingItem =
              (showGross && formattedPrice.grossItem?.value) ||
              (showNet && formattedPrice.netItem?.value);

            return shouldShowPricingItem ? (
              <Fragment key={index}>
                <TooltipPriceLabelWrapper>
                  {formattedPrice.netItem.description ? (
                    <>
                      <Tooltip
                        content={
                          formattedPrice.netItem.description
                            ? formattedPrice.netItem.description
                            : t('No additional info')
                        }
                      >
                        <Icon svg={Info} size={20} color={COLORS.GREEN} />
                      </Tooltip>
                    </>
                  ) : (
                    <Margin20 />
                  )}
                  <PriceLabel>
                    {`${
                      formattedPrice.name
                        ? formattedPrice.name
                        : formattedPrice.key.replace('P_', '')
                    }:`}
                  </PriceLabel>
                </TooltipPriceLabelWrapper>
                {showGross && (
                  <PriceLabelValue>
                    {formattedPrice.grossItem?.value
                      ? formatMoney(formattedPrice.grossItem?.value)
                      : '/'}
                  </PriceLabelValue>
                )}
                {showNet && showGross && (
                  <DiscountWrapper hasDiscount={shouldShowDiscountColumn}>
                    <DiscountValue>
                      {getDiscountDisplay(formattedPrice.netItem)}
                    </DiscountValue>
                  </DiscountWrapper>
                )}
                {showNet && (
                  <NetPriceWrapper>
                    <PriceLabelValue>
                      {formattedPrice.netItem?.value
                        ? formatMoney(formattedPrice.netItem?.value)
                        : '/'}
                    </PriceLabelValue>

                    {showNet && showGross && canEditPricing && (
                      <Icon
                        svg={PencilSimple}
                        size={20}
                        weight="regular"
                        color={COLORS.GREEN}
                        onClick={() => {
                          setEditingKey(formattedPrice.key);
                          setApplyToAll(false);
                          setExistingDiscount({
                            type: formattedPrice.netItem.custom_amount
                              ? DiscountsType.AMOUNT
                              : DiscountsType.PERCENTAGE,
                            value:
                              formattedPrice.netItem.custom_amount?.toString() ||
                              (formattedPrice.netItem.custom_discount != null
                                ? (
                                    formattedPrice.netItem.custom_discount * 100
                                  ).toString()
                                : null),
                            gross: formattedPrice.grossItem.value?.toString(),
                          });
                          setIsEditDiscountModalOpen(true);
                        }}
                      />
                    )}
                  </NetPriceWrapper>
                )}
              </Fragment>
            ) : null;
          }
        )}
      </GridContainer>
      {showNet && showGross && canEditPricing && (
        <CustomNetPriceWrapper onClick={() => setIsFinalPriceModalOpen(true)}>
          <CustomNetPriceLabel>{t('Set final net price')}</CustomNetPriceLabel>
        </CustomNetPriceWrapper>
      )}

      {isEditDiscountModalOpen && (
        <EditDiscountModal
          setIsOpen={setIsEditDiscountModalOpen}
          isOpen={isEditDiscountModalOpen}
          onConfirm={handleConfirmDiscount}
          onReset={handleResetDiscount}
          defaultValue={existingDiscount.value}
          defaultType={existingDiscount.type}
          grossPrice={existingDiscount.gross}
          setApplyToAll={setApplyToAll}
          applyToAll={applyToAll}
        />
      )}

      {isFinalPriceModalOpen && (
        <FinalPriceModal
          setIsOpen={setIsFinalPriceModalOpen}
          isOpen={isFinalPriceModalOpen}
          onConfirm={handleFinalPrice}
          totalPrice={totalPrice}
        />
      )}
    </ContainerAbsolute>
  );
};

export default PricingSection;
